import type { CalendarView } from "./calendarPageContext";

/**
 * What the user is currently looking at, handed to the chat endpoint with each
 * turn so the assistant can resolve "here" and "this customer".
 *
 * Two properties make this safe rather than merely convenient:
 *
 * - Only ids and closed enums travel. Anything free-text the client can put in
 *   the request ends up in the system prompt, which is the same hole
 *   `chat-messages.validate.ts` closes by rejecting client `system` messages.
 * - `label` is display-only and never leaves the browser, so the split is
 *   structural instead of a rule someone has to remember.
 */
export type AiChatPageContext =
  | {
      page: "calendar";
      locationId: number | null;
      view: CalendarView;
      // The span on screen, so the assistant can pass it straight to
      // `list_appointments` instead of working out which week a date falls in.
      // Equal to `dateFrom` in day view.
      dateFrom: string;
      dateTo: string;
    }
  | { page: "customer-detail"; customerId: number };

export type PublishedPageContext = {
  payload: AiChatPageContext;
  label: string;
};

export type PageContextOwner = { readonly id: symbol };

/**
 * A stack, not a single slot. A customer card opens as a drawer over the
 * calendar (`CalendarModalWrapper`, `Waitlist`), so two pages are mounted at
 * once: the innermost wins while it is open, and closing it restores the one
 * underneath. A single slot lost the calendar's context on drawer close,
 * because its publisher has no reason to re-run.
 */
const entries = new Map<PageContextOwner, PublishedPageContext>();
const listeners = new Set<() => void>();
let dismissed: string | null = null;

export function createPageContextOwner(): PageContextOwner {
  return { id: Symbol("ai-chat-page-context") };
}

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

function topEntry(): PublishedPageContext | null {
  let last: PublishedPageContext | null = null;
  for (const published of entries.values()) {
    last = published;
  }
  return last;
}

let snapshot: PublishedPageContext | null = null;

/**
 * `useSyncExternalStore` compares snapshots by reference and re-renders forever
 * if a fresh object comes back each read, so the resolved value is cached and
 * only replaced when the underlying stack actually changes.
 */
function refresh() {
  const top = topEntry();
  const next =
    top === null || JSON.stringify(top.payload) === dismissed ? null : top;
  if (next !== snapshot) {
    snapshot = next;
    notify();
  }
}

export function publishAiChatPageContext(
  owner: PageContextOwner,
  published: PublishedPageContext,
) {
  // Re-insert so the most recently published page is the top of the stack even
  // when it was already registered.
  entries.delete(owner);
  entries.set(owner, published);
  refresh();
}

export function clearAiChatPageContext(owner: PageContextOwner) {
  if (entries.delete(owner)) {
    refresh();
  }
}

export function readAiChatPageContext(): PublishedPageContext | null {
  return snapshot;
}

export function subscribeToAiChatPageContext(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The user dismissing the chip, which no page owns. It suppresses this exact
 * context rather than clearing the stack: the page is still open, so a
 * publisher has nothing to re-run, and navigating or picking another location
 * offers a different context that is shown again.
 */
export function dismissAiChatPageContext() {
  const top = topEntry();
  dismissed = top === null ? null : JSON.stringify(top.payload);
  refresh();
}

/** Test seam — module state outlives a single test file otherwise. */
export function resetAiChatPageContext() {
  entries.clear();
  dismissed = null;
  refresh();
}
